
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { EventEmitter, Injectable, Injector, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpRequest, HttpEventType } from '@angular/common/http';
import {PlatformLocation} from '@angular/common';
import Util from './../util';
import { CustomMedia } from '../models/data-structures/custom-media/custom-media';
import { environment } from '../../environments/environment';
import { LcfsRin } from '../models/project/lcfs-rin.model';
import { RxStompService } from '@stomp/ng2-stompjs';
import { ConfiguredToastrService } from '../helper/toastr/configured-toastr.service';
import { ProgressMessage } from '../models/data-structures/progress-message.model';
import { UserMetrics, UserMetricsInterface } from '../models/user-metrics.model';
import { WarningsInfo, WarningsInfoInterface } from '../models/warningsinfo.model';
import { AnessaTranslationService } from '../helper/translation/anessa-translation.service';
import { JavaCapexEstimate } from '../models/data-structures/java-capex-estimate.model';

@Injectable()
export class CommunicationService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();
  private readonly baseHref: string;
  private jsonHeader = new HttpHeaders({'Content-Type': 'application/json'});
  public newCustomMediaUploaded: Subject<CustomMedia> = new Subject<CustomMedia>();
  private errorSubscription?: Subscription;

  private routerEventsSubscription?: Subscription;
  private currentPage?: string;
  private startTime: number = Date.now();
  private userMetrics?: UserMetrics;
  private saveMetricsIntervalId: any;
  private saveMetricsInterval = 60;

  private socketSubscription?: Subscription;
  public progressNotifier = new BehaviorSubject<ProgressMessage>(null);
  public destroyMetricsNotifier = new EventEmitter();
  public simulationCompleteNotifier = new EventEmitter<{ completed: boolean, project: string }>();
  translationService?: AnessaTranslationService;


  private warningsInfoalerts?: WarningsInfo;
  private warningsInfounit?: WarningsInfo;

  constructor(
    private http: HttpClient,
    private injector: Injector,
    private router: Router,
    private platformLocation: PlatformLocation,
    private toastrService: ConfiguredToastrService,
  ) {
    setTimeout(() => this.translationService = this.injector.get(AnessaTranslationService));

    this.baseHref = this.platformLocation.getBaseHrefFromDOM();
    this.destroyMetricsNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        if (this.routerEventsSubscription) this.routerEventsSubscription.unsubscribe();
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  postRequest(message: any, url: string) {
    const body = JSON.stringify(message);
    const urlFixed = Util.prepURL(url, this.baseHref);

    return this.http.post(urlFixed, body, {headers: this.jsonHeader});
  }

  putRequest(message: any, url: string) {
    const body = JSON.stringify(message);
    const urlFixed = Util.prepURL(url, this.baseHref);

    return this.http.put(urlFixed, body, {headers: this.jsonHeader});
  }

  postRequestNoPopup(message: any, url: string) {
    const body = JSON.stringify(message);
    const urlFixed = Util.prepURL(url, this.baseHref);

    return this.http.post(urlFixed, body, {headers: this.jsonHeader});
  }

  fileRequest(postpack: any, url: string) {

    const urlFixed = Util.prepURL(url, this.baseHref);
    const body = JSON.stringify(postpack);
    return this.http.post(urlFixed, body, {headers: this.jsonHeader, responseType: 'blob'}).pipe(
      map((res) => {
        return res;
      }));

  }

  deleteRequest(id: string, url: string) {
    const urlFixed = Util.prepURL(url + '/' + id, this.baseHref);

    return this.http.delete(urlFixed, {headers: this.jsonHeader});
  }

  getRequest(id: string, url: string) {
    const urlFixed = Util.prepURL(url + '/' + id, this.baseHref);

    return this.http.get(urlFixed, {headers: this.jsonHeader});
  }

  getAllRequest(url: string) {
    const urlFixed = Util.prepURL(url, this.baseHref);

    return this.http.get(urlFixed, {headers: this.jsonHeader});
  }

  saveWarningsInfo(alertList: any, plantId: string, warningsdate: string) {
    if (!this.translationService) return;
    console.log('saving to WarningsInfo events');
    const userId = localStorage.getItem('userID');
    const accountId = localStorage.getItem('accountID');
    if (userId && accountId && alertList.length>0) {
      for(let i=0;i<alertList.length ;i++){
        if(this.warningsInfoalerts && this.warningsInfoalerts.warning_date === warningsdate && this.warningsInfoalerts.warning_message!= this.translationService.get(alertList[i].message,alertList[i].params)){
          this.startTime = Date.now();
          this.warningsInfoalerts.plant = plantId;
          this.warningsInfoalerts.platform = 'ada';
          this.warningsInfoalerts.source = 'client';
          this.warningsInfoalerts.user = userId;
          this.warningsInfoalerts.warning_date = warningsdate;
          this.warningsInfoalerts.account = localStorage.getItem('accountID');
          this.warningsInfoalerts.warning_message = this.translationService.get(alertList[i].message,alertList[i].params);
          this.putRequest(this.warningsInfoalerts, `/dbapi/warningsInfo/${userId}/${warningsdate}`)
            .pipe(take(1))
            .subscribe();
        } else {
          (this.getRequest(warningsdate, '/dbapi/warningsInfo/' + userId) as Observable<{obj: WarningsInfoInterface }>)
            .pipe(take(1))
            .subscribe(result => {
              if (!this.translationService) return;
              this.warningsInfoalerts = new WarningsInfo(result['obj']);
              this.warningsInfoalerts.platform = 'ada';
              this.warningsInfoalerts.source = 'client';
              this.warningsInfoalerts.warning_date = warningsdate;
              this.warningsInfoalerts.account = localStorage.getItem('accountID');
              this.warningsInfoalerts.warning_message = this.translationService.get(alertList[i].message,alertList[i].params);
              this.warningsInfoalerts.plant = plantId;
              this.warningsInfoalerts.user = userId;
              this.putRequest(this.warningsInfoalerts, `/dbapi/warningsInfo/${userId}/${warningsdate}`)
                .pipe(take(1))
                .subscribe();
            });
        }
      }
    }
  }



saveWarningsInfoFromUnitvalue(warning: string, plantId: string, warningsdate: string) {
  console.log('saving to WarningsInfo events');
  const userId = localStorage.getItem('userID');
  const accountId = localStorage.getItem('accountID');
  if (userId && accountId && warning !== '') {
    if(this.warningsInfounit && this.warningsInfounit.warning_date === warningsdate && this.warningsInfounit.warning_message!==warning){
      this.startTime = Date.now();
      this.warningsInfounit.plant = plantId;
      this.warningsInfounit.user = userId;
      this.warningsInfounit.warning_date = warningsdate;
      this.warningsInfounit.account = localStorage.getItem('accountID');
      this.warningsInfounit.warning_message = warning;
      this.putRequest(this.warningsInfounit, `/dbapi/warningsInfo/${userId}/${warningsdate}`)
        .pipe(take(1))
        .subscribe();
    } else {
    (this.getRequest(warningsdate, '/dbapi/warningsInfo/' + userId) as Observable<{ obj: WarningsInfoInterface }>)
      .pipe(take(1))
      .subscribe(result => {
        this.warningsInfounit = new WarningsInfo(result['obj']);
        this.warningsInfounit.warning_date = warningsdate;
        this.warningsInfounit.account = localStorage.getItem('accountID');
        this.warningsInfounit.warning_message = warning;
        this.warningsInfounit.plant = plantId;
        this.warningsInfounit.user = userId;
        this.putRequest(this.warningsInfounit, `/dbapi/warningsInfo/${userId}/${warningsdate}`)
            .pipe(take(1))
            .subscribe();
      });
    }
  }
}

  uploadImage(file: File, name: string, graphDataId: string,
              path: string, caption?: string | null, purpose?: string): Observable<number | string> {
    const fixedUrl = Util.prepURL(path, this.baseHref);
    const fd = new FormData();

    fd.append('image', file, file.name);
    if (caption) {
      fd.append('caption', caption);
    }
    fd.append('name', name);

    const projID = localStorage.getItem('projectID');
    if (projID) {
      fd.append('projID', projID);
    }
    fd.append('graphDataId', graphDataId);
    if (purpose) {
      fd.append('purpose', purpose);
    }

    const req = new HttpRequest('POST', fixedUrl, fd, {reportProgress: true});
    const progress = new Subject<number | string>();
    const upDoneNotifier = new EventEmitter();
    this.http.request(req).pipe(takeUntil(upDoneNotifier)).subscribe(event => {
      console.log(event);
      // handle progress bar
      if (event.type === HttpEventType.UploadProgress) {
        if (event.total) {
          const percentDone = Math.round(100 * event.loaded / event.total);
          progress.next(percentDone);
          if (percentDone === 100) {
            // upDoneNotifier.emit();
            console.log('upload done emitted');
            // upDoneNotifier.unsubscribe();
          }
        }
      } else if (event instanceof HttpResponse) {
        upDoneNotifier.emit();
        console.log('upload done emitted');
        upDoneNotifier.unsubscribe();
        const obj = (event.body as any)?.obj;
        if (obj) {
          if (obj['absolutePath']) {
            progress.next(obj['absolutePath']);
          } else {
            progress.next(100);
            this.newCustomMediaUploaded.next(new CustomMedia(obj));
          }
        }
        progress.complete();
      }
    });
    return progress.asObservable();
  }

  private saveUserMetrics(userId: string) {
    const elapsedTime = Date.now() - this.startTime;
    this.startTime = Date.now();

    if (!this.userMetrics || !this.currentPage) return;

    // Save time since last route change or save event in milliseconds
    if (!this.userMetrics.pageVisits[this.currentPage]) {
      this.userMetrics.pageVisits[this.currentPage] = 0;
    }
    this.userMetrics.pageVisits[this.currentPage] += elapsedTime;

    // Add the interval time to user logged in time (crude, but effective ¯\_(ツ)_/¯)
    if (!this.userMetrics.timeSpentLoggedIn) {
      this.userMetrics.timeSpentLoggedIn = 0;
    }
    this.userMetrics.timeSpentLoggedIn += this.saveMetricsInterval;

    this.putRequest(this.userMetrics, '/dbapi/metrics/' + userId)
      .pipe(take(1))
      .subscribe();
  }

  private incrementMetricsCount(userId: string, name: string) {
    this.putRequest(this.userMetrics, `/dbapi/metrics/${userId}/increment?name=${name}`)
      .pipe(take(1))
      .subscribe();
  }

  private decrementMetricsCount(userId: string, name: string) {
    this.putRequest(this.userMetrics, `/dbapi/metrics/${userId}/decrement?name=${name}`)
      .pipe(take(1))
      .subscribe();
  }

  connectToMetrics() {
    // Subscribe to naviation events
    console.log('connecting to navigation events');
    this.startTime = Date.now();
    if (this.routerEventsSubscription) this.routerEventsSubscription.unsubscribe();
    this.routerEventsSubscription = this.router.events.subscribe(event => {
      const userId = localStorage.getItem('userID');
      const accountId = localStorage.getItem('accountID');
      if (event instanceof NavigationEnd) {
        const elapsedTime = Date.now() - this.startTime;
        // Only track events when user logged in
        if (userId && accountId) {
          if (!this.userMetrics) {
            (this.getRequest(userId, '/dbapi/metrics/') as Observable<{ obj: UserMetricsInterface }>)
              .pipe(take(1))
              .subscribe(result => {
                console.log('initializing user metrics');
                this.userMetrics = new UserMetrics(result.obj);
                this.userMetrics.accountId = accountId;

                // save user metrics every 60 seconds
                clearInterval(this.saveMetricsIntervalId);
                this.saveMetricsIntervalId = setInterval(this.saveUserMetrics.bind(this, userId), this.saveMetricsInterval * 1000);
              });
          } else {
            if (!this.currentPage) return;
            // Save time since last route change or save event in milliseconds
            if (!this.userMetrics.pageVisits[this.currentPage]) {
              this.userMetrics.pageVisits[this.currentPage] = 0;
            }
            this.userMetrics.pageVisits[this.currentPage] += elapsedTime;
          }
        }
        this.startTime = Date.now();
        this.currentPage = event.urlAfterRedirects;
      }
    });
  }

  disconnectFromMetrics() {
    console.log('disconnecting from user metrics');
    if (this.routerEventsSubscription !== undefined && this.routerEventsSubscription !== null) {
      this.routerEventsSubscription.unsubscribe();
      this.routerEventsSubscription = undefined;
    }
    if (this.saveMetricsIntervalId !== undefined && this.saveMetricsIntervalId !== null) {
      clearInterval(this.saveMetricsIntervalId);
      this.saveMetricsIntervalId = undefined;
    }
    this.userMetrics = undefined;
  }

  /**
   * Initiates the capex opex estimation from Java. The opex percentage is only needed when specifically computing for
   * opex, the 0 will satisfy the computation.
   * @param opexPercentageValue is the opex estimation percentage of total capex.
   */
  estimateCapex(opexPercentageValue: number = 0) {
    const url = '/ada/capex-estimate';
    return (this.http.post(
      environment.simulatorURL + url + '?objectID=' + localStorage.getItem('projectID') +
          '&opexPercentageValue=' + encodeURIComponent(opexPercentageValue),
      {},
      {headers: this.jsonHeader}
    ) as Observable<JavaCapexEstimate>);
  }

  /**
   * Returns a subscription to a call to the Java server for fetching
   * the thin/whole digestate production per year, in m3.
   */
  fetchDigestateM3ProductionPerYear() {
    const url = '/ada/expense-digestate-prod';
    return this.http.post(
      environment.simulatorURL + url + '?objectID=' + localStorage.getItem('projectID'),
      {},
      {headers: this.jsonHeader}
    ) as Observable<{
      'whole digestate biofertilizer': number,
      'solid digestate biofertilizer': number,
      'thin digestate biofertilizer': number,
      'productRates': Record<string, number>,
      'consumedFuels': Record<string, number>
    }>;
  }

  /**
   * Returns a subscription to a call to the Java server for fetching
   * the thin/whole digestate production per year, in m3.
   */
  fetchBiomethanePotential() {
    const url = '/ada/compute-technical';
    return this.http.post(
      environment.simulatorURL + url + '?objectID=' + localStorage.getItem('projectID'),
      {},
      {headers: this.jsonHeader}
    ) as Observable<{ ch4VolFlowPerYear: number, bmpConcentration: number }>;
  }

  calculateLcfsRin(productID: string, lcfsRin: LcfsRin) {
    const url = '/ada/calc-lcfs-rin';
    return this.http.post(
      environment.simulatorURL + url + '?objectID=' + localStorage.getItem('projectID')
            + '&productID=' + productID
            + '&lcfsrinobj=' + encodeURIComponent(JSON.stringify(lcfsRin)),
      {},
      {headers: this.jsonHeader}
    );
  }


  calculateDesiredX(slope: number, gradient: number, expectedY: number , xvals : any, yvals: any) {
    console.log("Inside communications")
    const url = '/ada/sensitivity-solver';
    return this.http.post(
      environment.simulatorURL + url + '?objectID=' + localStorage.getItem('projectID')
      + '&slope=' + slope
      + '&gradient=' + gradient
      + '&expectedY=' + expectedY
      + '&xvals=' + xvals
      + '&yvals=' + yvals,
      {},
      {headers: this.jsonHeader}
    );
  }

  performScenarioComparisonMetricsSave() {
    const userId = localStorage.getItem('userID');
    if (!userId) return;
    this.incrementMetricsCount(userId, 'saveScenarioComparisonCount');
    this.decrementMetricsCount(userId, 'saveCount');
  }

  performSensitivityAnalysisMetricsSave() {
    const userId = localStorage.getItem('userID');
    if (!userId) return;
    this.incrementMetricsCount(userId, 'saveSensitivityCount');
    this.decrementMetricsCount(userId, 'saveCount');
  }

  performInterpolation(values: string) {
    const url = `/ada/interpolate?vals=${encodeURIComponent(values)}`;
    return (this.http.post(environment.simulatorURL + url, {}) as Observable<number[][]>);
  }

  performScenarioSimulation() {
    const userID = localStorage.getItem('userID');
    if (this.userMetrics && userID) {
      this.userMetrics.scenarioComparisonCount += 1;
      this.incrementMetricsCount(userID, 'scenarioComparisonCount');
    }

    const url = '/ada/simulate-single-scenario';
    return this.http.post(
      environment.simulatorURL + url + '?projID=' + localStorage.getItem('projectID'),
      {},
      {headers: this.jsonHeader,  responseType: 'text'}
    );
  }

  preformSensitivityAnalysis() {
    const userID = localStorage.getItem('userID');
    if (this.userMetrics && userID) {
      this.userMetrics.sensitivityCount += 1;
      this.incrementMetricsCount(userID, 'sensitivityCount');
    }

    const url = '/ada/sensitivity';
    return this.http.post(
      environment.simulatorURL + url + '?projID=' + localStorage.getItem('projectID'),
      {},
      {headers: this.jsonHeader, responseType: 'text'});
  }


  /**
   * ADA doesn't use progress updates at the moment,
   * see ADO for working example w/ translations
   */
  public connectToSocket(rxStompService: RxStompService) {
    // process.env["NODE_TLS_REJECT_UNAUTHORIZED"] = 0;
    if (localStorage.getItem('accountID') === null) {
      return;
    }
    this.socketSubscription = rxStompService.watch(
      '/ada/progress/' + localStorage.getItem('userID'),
      {'rejectUnauthorized': '0'})
      .subscribe((message) => {
        const msg: ProgressMessage = JSON.parse(message.body);
        this.progressNotifier.next(msg);
      });
  }

  subscribeToErrors(rxStompService: RxStompService) {
    const accountId = localStorage.getItem('accountID');
    const userId = localStorage.getItem('userID');
    if (!accountId) {
      return;
    }

    this.errorSubscription = rxStompService.watch(
      '/error/' + accountId + '/' + userId,
      {'rejectUnauthorized': '0'})
      .subscribe((message) => {
        const msg = JSON.parse(message.body);
        this.toastrService.error((msg['errorMessage']));
      });
    }

  disconnectFromSocket() {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
    }
  }

  storeBlobAsFileSilently(blob: any, imageName: string, purpose?: string) {
    const file = new File(
      [blob],
      imageName + '.png',
      {
        type: 'image/png',
        lastModified: Date.now()
      }
    );
    return this.uploadImage(file, imageName, 'no-data-id',
      'wsasapi/report/saveImage', null, purpose);
  }

  fetchReachedProjectLimit() {
    const accountId = localStorage.getItem('accountID');
    const urlFixed = Util.prepURL(`/wsasapi/project/reachedProjectLimit/?accountId=${accountId}`, this.baseHref);

    return (this.http.get(urlFixed, {headers: this.jsonHeader}) as Observable<{ obj: {
      reachedProjectLimit: boolean,
      reachedScreeningProjectLimit: boolean
    }}>)
      .pipe(take(1))
      .subscribe(result => {
        localStorage.setItem('reachedProjectLimit', result.obj.reachedProjectLimit+'');
        localStorage.setItem('reachedScreeningProjectLimit', result.obj.reachedScreeningProjectLimit+'');
      });
  }
}
